import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

// @mui material components
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDAvatar from "components/MDAvatar";

// Material Dashboard 2 React icons
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";

/**
 * ClientDetailsModal component for viewing and editing client details.
 * Supports both individual and entity client types.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Object} props.client - Client data object
 * @param {string} props.clientType - Type of client ('individual' or 'entity')
 * @param {Function} props.onSave - Function to call when saving changes
 * @param {boolean} props.loading - Loading state for save operation
 */
function ClientDetailsModal({ open, onClose, client, clientType, onSave, loading = false, onFetchClient }) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [fetchingClient, setFetchingClient] = useState(false);
  const [clientData, setClientData] = useState(null);

  /**
   * Initialize form data from client object.
   * @param {Object} currentClient - Client data object
   */
  const initializeFormData = React.useCallback((currentClient) => {
    if (!currentClient) return;
    
    if (clientType === 'individual') {
      setFormData({
        name: currentClient.full_name || "",
        nickname: currentClient.nickname || "",
        id_number: currentClient.id_number || "",
        email: currentClient.email || "",
        phone: currentClient.phone || "",
        address: currentClient.address || "",
        tax_number: currentClient.tax_number || "",
        date_of_birth: currentClient.date_of_birth || "",
      });
    } else if (clientType === 'entity') {
      setFormData({
        name: currentClient.full_name || "",
        legal_name: currentClient.legal_name || "",
        registration_number: currentClient.registration_number || "",
        license_number: currentClient.license_number || "",
        email: currentClient.email || "",
        phone: currentClient.phone || "",
        address: currentClient.address || "",
      });
    }
  }, [clientType]);

  // Fetch client data when modal opens
  useEffect(() => {
    if (open && client && client.id && onFetchClient) {
      setFetchingClient(true);
      onFetchClient(client.id, clientType)
        .then((data) => {
          setClientData(data);
        })
        .catch((error) => {
          console.error('Error fetching client data:', error);
          // Fallback to using the passed client data
          setClientData(client);
        })
        .finally(() => {
          setFetchingClient(false);
        });
    } else if (open && client) {
      // If no fetch function provided, use the passed client data
      setClientData(client);
    }
  }, [open, client, clientType, onFetchClient]);

  // Initialize form data when client data changes
  useEffect(() => {
    const currentClient = clientData || client;
    if (currentClient) {
      initializeFormData(currentClient);
      setErrors({});
      setIsEditMode(false);
    }
  }, [clientData, client, clientType, initializeFormData]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancel = () => {
    // Reset form data to original client data
    const currentClient = clientData || client;
    initializeFormData(currentClient);
    setErrors({});
    setIsEditMode(false);
  };

  const handleSave = () => {
    // Validate required fields
    const newErrors = {};
    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Name is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Call onSave with form data
    if (onSave) {
      onSave(formData);
    }
  };

  const getInitials = (name) => {
    if (!name) return '';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const currentClient = clientData || client;
  if (!currentClient) return null;

  // Show loading state while fetching
  if (fetchingClient) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <MDTypography variant="body2" ml={2}>
              Loading client details...
            </MDTypography>
          </MDBox>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDTypography variant="h6">
            {clientType === 'individual' ? 'Individual Client' : 'Entity Client'} Details
          </MDTypography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </MDBox>
      </DialogTitle>

      <DialogContent>
        <MDBox py={2} display="flex" flexDirection="column" gap={2}>
          {/* Avatar and Name Section */}
          <MDBox display="flex" alignItems="center" gap={2} mb={2}>
            <MDAvatar bgColor="info" size="lg">
              {getInitials(currentClient.full_name)}
            </MDAvatar>
            <MDBox flex={1}>
              {isEditMode ? (
                <MDInput
                  fullWidth
                  label="Name"
                  value={formData.name}
                  onChange={handleChange('name')}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                />
              ) : (
                <MDTypography variant="h5" fontWeight="medium">
                  {currentClient.full_name}
                </MDTypography>
              )}
            </MDBox>
          </MDBox>

          {/* Individual Client Fields */}
          {clientType === 'individual' && (
            <>
              <MDInput
                fullWidth
                label="Nickname"
                value={formData.nickname || ""}
                onChange={handleChange('nickname')}
                disabled={!isEditMode}
                error={!!errors.nickname}
                helperText={errors.nickname}
              />

              <MDBox display="flex" gap={2}>
                <MDInput
                  fullWidth
                  label="ID/Passport Number"
                  value={formData.id_number || ""}
                  onChange={handleChange('id_number')}
                  disabled={!isEditMode}
                  error={!!errors.id_number}
                  helperText={errors.id_number}
                />
                <MDInput
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email || ""}
                  onChange={handleChange('email')}
                  disabled={!isEditMode}
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </MDBox>

              <MDBox display="flex" gap={2}>
                <MDInput
                  fullWidth
                  label="Phone"
                  value={formData.phone || ""}
                  onChange={handleChange('phone')}
                  disabled={!isEditMode}
                  error={!!errors.phone}
                  helperText={errors.phone}
                />
                <MDInput
                  fullWidth
                  label="Date of Birth"
                  type="date"
                  value={formData.date_of_birth || ""}
                  onChange={handleChange('date_of_birth')}
                  disabled={!isEditMode}
                  error={!!errors.date_of_birth}
                  helperText={errors.date_of_birth}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </MDBox>

              <MDInput
                fullWidth
                label="Address"
                value={formData.address || ""}
                onChange={handleChange('address')}
                disabled={!isEditMode}
                error={!!errors.address}
                helperText={errors.address}
                multiline
                rows={2}
              />

              <MDInput
                fullWidth
                label="Tax Number"
                value={formData.tax_number || ""}
                onChange={handleChange('tax_number')}
                disabled={!isEditMode}
                error={!!errors.tax_number}
                helperText={errors.tax_number}
              />
            </>
          )}

          {/* Entity Client Fields */}
          {clientType === 'entity' && (
            <>
              <MDInput
                fullWidth
                label="Legal Name"
                value={formData.legal_name || ""}
                onChange={handleChange('legal_name')}
                disabled={!isEditMode}
                error={!!errors.legal_name}
                helperText={errors.legal_name}
              />

              <MDBox display="flex" gap={2}>
                <MDInput
                  fullWidth
                  label="Registration Number"
                  value={formData.registration_number || ""}
                  onChange={handleChange('registration_number')}
                  disabled={!isEditMode}
                  error={!!errors.registration_number}
                  helperText={errors.registration_number}
                />
                <MDInput
                  fullWidth
                  label="License Number"
                  value={formData.license_number || ""}
                  onChange={handleChange('license_number')}
                  disabled={!isEditMode}
                  error={!!errors.license_number}
                  helperText={errors.license_number}
                />
              </MDBox>

              <MDBox display="flex" gap={2}>
                <MDInput
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email || ""}
                  onChange={handleChange('email')}
                  disabled={!isEditMode}
                  error={!!errors.email}
                  helperText={errors.email}
                />
                <MDInput
                  fullWidth
                  label="Phone"
                  value={formData.phone || ""}
                  onChange={handleChange('phone')}
                  disabled={!isEditMode}
                  error={!!errors.phone}
                  helperText={errors.phone}
                />
              </MDBox>

              <MDInput
                fullWidth
                label="Address"
                value={formData.address || ""}
                onChange={handleChange('address')}
                disabled={!isEditMode}
                error={!!errors.address}
                helperText={errors.address}
                multiline
                rows={2}
              />
            </>
          )}
        </MDBox>
      </DialogContent>

      <DialogActions>
        <MDBox display="flex" gap={1} width="100%" justifyContent="flex-end" p={2}>
          {isEditMode ? (
            <>
              <MDButton
                variant="outlined"
                color="secondary"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </MDButton>
              <MDButton
                variant="contained"
                color="info"
                onClick={handleSave}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : null}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </MDButton>
            </>
          ) : (
            <>
              <MDButton
                variant="outlined"
                color="secondary"
                onClick={onClose}
              >
                Close
              </MDButton>
              <MDButton
                variant="contained"
                color="info"
                onClick={handleEdit}
                startIcon={<EditIcon />}
              >
                Edit
              </MDButton>
            </>
          )}
        </MDBox>
      </DialogActions>
    </Dialog>
  );
}

ClientDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  client: PropTypes.shape({
    id: PropTypes.string,
    full_name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    address: PropTypes.string,
    // Individual client fields
    id_number: PropTypes.string,
    tax_number: PropTypes.string,
    date_of_birth: PropTypes.string,
    nickname: PropTypes.string,
    // Entity client fields
    legal_name: PropTypes.string,
    registration_number: PropTypes.string,
    license_number: PropTypes.string,
  }),
  clientType: PropTypes.oneOf(['individual', 'entity']).isRequired,
  onSave: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  onFetchClient: PropTypes.func,
};

export default ClientDetailsModal;

